/**
 * Boundary between the form's `Rules` shape and the backend `RuleSet`
 * shapes (`RuleSetType` for reads, `RuleSetInput` for writes).
 *
 * Hoisted out of `QuerySettingsPanel/settingsMapper.ts` so both
 * `QuerySettingsPanel` (query-level rules) and `FieldSettingsPanel`
 * (field-level filter / suppression rules) consume the same mappers —
 * neither panel reaches into the other's mapper.
 *
 * `value: JSON!` on the BE is normalised against the **operator** so a
 * scalar landing under `IN`/`NOT_IN` (or vice versa) can't sneak through
 * to the form state — see `normalizeClauseValue` for the recovery
 * heuristics.
 */

import { v4 as uuidv4 } from "uuid";
import { normalizeAttributePath } from "./attributeCatalogue";
import { DEFAULT_GROUPS_OPERATOR } from "./constants";
import { SET_OPERATORS } from "./types";
import type {
  ConjunctionOperator,
  FilterOperator,
  RuleClause,
  RuleClauseValue,
  RuleGroup,
  Rules,
} from "./types";
import type {
  LogicalOperator,
  RuleConditionInput,
  RuleGroupInput,
  RuleOperator,
  RuleSetFieldsFragment,
  RuleSetInput,
  RuleValueKind,
} from "~/graphql/supergraphTypes";

type BackendCondition = RuleSetFieldsFragment["groups"][number]["conditions"][number];

/** Operation payloads are a subset of `RuleSetType`. `valueKind` is required
 * on the schema object but omitted from any selection that hasn't picked it
 * up yet — treat missing as LITERAL. */
export type RuleConditionSource = {
  attribute: string;
  operator: RuleOperator;
  value: BackendCondition["value"];
  valueKind?: RuleValueKind | null;
  sortOrder: number;
};

export type RuleSetSource = {
  groupsOperator: LogicalOperator;
  groups: ReadonlyArray<{
    sortOrder: number;
    conditionOperator: LogicalOperator;
    conditions: ReadonlyArray<RuleConditionSource>;
  }>;
};

// -- Enum maps ----------------------------------------------------------------

export const OPERATOR_TO_BE: Record<FilterOperator, RuleOperator> = {
  eq: "EQ",
  neq: "NEQ",
  gt: "GT",
  gte: "GTE",
  lt: "LT",
  lte: "LTE",
  in: "IN",
  not_in: "NOT_IN",
  contains: "CONTAINS",
};

export const OPERATOR_FROM_BE: Record<RuleOperator, FilterOperator> = {
  EQ: "eq",
  NEQ: "neq",
  GT: "gt",
  GTE: "gte",
  LT: "lt",
  LTE: "lte",
  IN: "in",
  NOT_IN: "not_in",
  CONTAINS: "contains",
};

export const CONJUNCTION_FROM_BE: Record<LogicalOperator, ConjunctionOperator> = {
  AND: "and",
  OR: "or",
};

export const CONJUNCTION_TO_BE: Record<ConjunctionOperator, LogicalOperator> = {
  and: "AND",
  or: "OR",
};

// -- Value normalisation ------------------------------------------------------

const isJsonScalar = (raw: unknown): raw is string | number | boolean =>
  typeof raw === "string" || typeof raw === "number" || typeof raw === "boolean";

/** Coerce one JSON value into a single scalar string (or `null`). Objects
 *  are JSON-stringified as a legible fallback so a malformed backend
 *  payload surfaces in the value editor instead of silently disappearing.
 */
const toScalarString = (raw: unknown): string | null => {
  if (raw === null || raw === undefined) return null;
  if (typeof raw === "string") return raw;
  if (typeof raw === "number" || typeof raw === "boolean") return String(raw);
  return JSON.stringify(raw);
};

/** Coerce one JSON value into a string array, dropping non-scalar
 *  elements rather than coercing them to `"[object Object]"` so an
 *  unexpected backend payload can't silently corrupt the form state.
 */
const toScalarArray = (raw: unknown): string[] => {
  if (!Array.isArray(raw)) return [];
  return raw.filter(isJsonScalar).map((item) => String(item));
};

/** Normalize the BE's `value: JSON!` into the `RuleClauseValue` union
 *  (`string | string[] | null`) using the **operator** to pick the
 *  expected shape — not the runtime payload alone. Mixing the two would
 *  let `{ operator: "IN", value: "asset" }` (or `{ operator: "EQ",
 *  value: ["x"] }`) into the form state, where the value editor would
 *  render empty (it expects the other shape) but `save` would still ship
 *  the original mis-shaped value verbatim.
 *
 *  Set ops (`IN`/`NOT_IN`) → array; scalar everything else → string. A
 *  scalar received under a set operator is auto-wrapped, and an array
 *  received under a scalar operator collapses to its first element
 *  (best-effort recovery from drifted data; `null` if the array is
 *  empty / all-non-scalar).
 */
export const normalizeClauseValue = (
  raw: unknown,
  operator: FilterOperator,
): RuleClauseValue => {
  if (raw === null || raw === undefined) return null;
  if (SET_OPERATORS.has(operator)) {
    if (Array.isArray(raw)) return toScalarArray(raw);
    const scalar = toScalarString(raw);
    return scalar === null ? [] : [scalar];
  }
  if (Array.isArray(raw)) {
    const arr = toScalarArray(raw);
    return arr.length > 0 ? arr[0] : null;
  }
  return toScalarString(raw);
};

// -- Read path: backend → form -----------------------------------------------

const toClause = (condition: RuleConditionSource): RuleClause => {
  const operator = OPERATOR_FROM_BE[condition.operator];
  const valueKind = condition.valueKind ?? "LITERAL";
  return {
    id: uuidv4(),
    attribute: normalizeAttributePath(condition.attribute),
    operator,
    // A runtime input's value is a token name, not a filter value, so it
    // bypasses the operator-driven scalar/array normalisation.
    value:
      valueKind === "RUNTIME_INPUT"
        ? toScalarString(condition.value)
        : normalizeClauseValue(condition.value, operator),
    valueKind,
  };
};

const toGroup = (group: RuleSetSource["groups"][number]): RuleGroup => ({
  id: uuidv4(),
  conditionOperator: CONJUNCTION_FROM_BE[group.conditionOperator],
  clauses: [...group.conditions].sort((a, b) => a.sortOrder - b.sortOrder).map(toClause),
});

export const toRules = (ruleSet: RuleSetSource | null | undefined): Rules => {
  if (!ruleSet) return { groupsOperator: DEFAULT_GROUPS_OPERATOR, groups: [] };
  return {
    groupsOperator: CONJUNCTION_FROM_BE[ruleSet.groupsOperator],
    groups: [...ruleSet.groups].sort((a, b) => a.sortOrder - b.sortOrder).map(toGroup),
  };
};

// -- Write path: form → backend Input ----------------------------------------

const normalizeOutputValue = (value: RuleClauseValue): RuleClauseValue => {
  if (typeof value === "string") return value.trim();
  if (Array.isArray(value)) return value.map((item) => item.trim()).filter(Boolean);
  return value;
};

const toConditionInput = (clause: RuleClause, sortOrder: number): RuleConditionInput => ({
  attribute: clause.attribute ? normalizeAttributePath(clause.attribute) : "",
  operator: OPERATOR_TO_BE[clause.operator],
  // `value: JSON!` means present, not non-null — `null` is a valid scalar.
  // Codegen types JSON as `{ [key: string]: any }`, hence the cast.
  value: normalizeOutputValue(clause.value) as unknown as RuleConditionInput["value"],
  valueKind: clause.valueKind ?? "LITERAL",
  sortOrder,
});

const toGroupInput = (group: RuleGroup, sortOrder: number): RuleGroupInput => ({
  sortOrder,
  conditionOperator: CONJUNCTION_TO_BE[group.conditionOperator],
  conditions: group.clauses.map((clause, idx) => toConditionInput(clause, idx)),
});

export const toRulesInput = (rules: Rules): RuleSetInput => ({
  groupsOperator: CONJUNCTION_TO_BE[rules.groupsOperator],
  groups: rules.groups.map((group, idx) => toGroupInput(group, idx)),
});

// -- Write path: form → backend read-shape fragment ---------------------------
// `applyFieldSettingsToRaw` writes the rule edits back into the
// `QueryFieldFieldsFragment` raw companion so the existing field-level write
// pipeline (`toFieldSettingsInput` → `toRuleSetInput` in `fieldsMapper.ts`)
// picks them up on Save. That pipeline only reads `attribute` / `operator` /
// `value` / `valueKind` / `sortOrder` from each condition (and
// `conditionOperator` / `sortOrder` from each group), so the placeholder
// `id` / `__typename` fields we synthesize here are dropped before they hit
// the network.

const toConditionType = (
  clause: RuleClause,
  sortOrder: number,
): RuleSetFieldsFragment["groups"][number]["conditions"][number] => ({
  __typename: "RuleConditionType",
  id: 0,
  attribute: clause.attribute ? normalizeAttributePath(clause.attribute) : "",
  operator: OPERATOR_TO_BE[clause.operator],
  // BE `value` is `JSON!`. The form's union maps directly onto a JSON
  // scalar/array, so we hand it over verbatim.
  value: normalizeOutputValue(
    clause.value,
  ) as unknown as RuleSetFieldsFragment["groups"][number]["conditions"][number]["value"],
  valueKind: clause.valueKind ?? "LITERAL",
  sortOrder,
});

const toGroupType = (
  group: RuleGroup,
  sortOrder: number,
): RuleSetFieldsFragment["groups"][number] => ({
  __typename: "RuleGroupType",
  id: 0,
  sortOrder,
  conditionOperator: CONJUNCTION_TO_BE[group.conditionOperator],
  conditions: group.clauses.map((clause, idx) => toConditionType(clause, idx)),
});

export const toRuleSetType = (rules: Rules): RuleSetFieldsFragment => ({
  __typename: "RuleSetType",
  groupsOperator: CONJUNCTION_TO_BE[rules.groupsOperator],
  groups: rules.groups.map((group, idx) => toGroupType(group, idx)),
});
